// =============================================================================
// CALL-TO-ACTION SECTION COMPONENT
// =============================================================================

import React from 'react';
import { Button } from '@/components/Button';
import { cn } from '@/utils';
import { createActionHandler, convertButtonToAction } from '@/utils/actionHandler';
import type { ContentComponent } from '@/types';

interface CTASectionProps {
  component: ContentComponent;
}

/**
 * CTA Section Component
 */
export const CTASection: React.FC<CTASectionProps> = ({ component }) => {
  return <CTASectionImpl component={component} />;
};

/**
 * CTA Section Implementation
 */
const CTASectionImpl: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const buttons = metadata?.buttons || [];
  const backgroundImage = metadata?.backgroundImage;
  const imageCredit = metadata?.imageCredit;
  const textAlign = metadata?.textAlign || 'center';
  const overlayOpacity = metadata?.overlayOpacity || 0.4;
  const minHeight = metadata?.minHeight || '50vh';
  
  // Convert old button format to actions
  const actions = buttons.map(convertButtonToAction);
  
  const sectionStyle = backgroundImage ? {
    backgroundImage: `url(${backgroundImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    position: 'relative' as const,
    minHeight,
  } : {
    minHeight,
  };
  
  return (
    <section
      id={id}
      className={cn(
        'content-section cta-section',
        backgroundImage && 'cta-section--with-bg'
      )}
      style={sectionStyle}
    >
      {backgroundImage && (
        <div 
          className="cta-overlay"
          style={{ opacity: overlayOpacity }}
        />
      )}
      
      <div className="container">
        <div
          className={cn(
            'cta-content',
            `cta-content--${textAlign}`
          )}
        >
          {title && (
            <h2 className="cta-title heading-style">{title}</h2>
          )}
          {content && (
            <p className="cta-description body-style">{content}</p>
          )}
          
          {actions.length > 0 && (
            <div className="cta-actions">
              {actions.map((action, index) => (
                <Button
                  key={index}
                  variant={action.variant || 'primary'}
                  size={action.size || 'lg'}
                  className={cn(
                    'cta-button',
                    `cta-button--${action.variant || 'primary'}`
                  )}
                  onClick={createActionHandler(action)}
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Image Credit */}
      {imageCredit && backgroundImage && (
        <div className="cta-image-credit">
          <span dangerouslySetInnerHTML={{ __html: imageCredit }} />
        </div>
      )}
    </section>
  );
};

export { CTASectionImpl };

